import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const FinancialVerificationContext = createContext();

const NONPROFIT_CORPORATION = 'Nonprofit Corporation';

export const DOCUMENT_DEFINITIONS = {
  tax_exempt: {
    id: 'tax_exempt',
    title: 'Tax Exempt Documentation',
    description:
      'IRS 990 letter, 501c3 letter, or other IRS tax exempt documentation.',
    isRequired: true,
  },
  financial_statements_1: {
    id: 'financial_statements_1',
    title: 'Prior 1 Year Financial Statements',
    description:
      'Could include documents such as audited financial statements, business tax returns or cash flow statements.',
    isRequired: true,
  },
  financial_statements_2: {
    id: 'financial_statements_2',
    title: 'Prior 2 Years Financial Statements',
    description:
      'Could include documents such as audited financial statements. business tax returns or cash flow statements.',
    isRequired: true,
  },
  ytd_balance_sheet: {
    id: 'ytd_balance_sheet',
    title: 'Current YTD Balance Sheet',
    isRequired: true,
  },
  ytd_profit_loss: {
    id: 'ytd_profit_loss',
    title: 'Current YTD Profit & Loss Statement',
    isRequired: true,
  },
  bank_statements: {
    id: 'bank_statements',
    title:
      '3 months bank statements from primary operating account for the organization.',
    isRequired: true,
  },
};

const TCL_TIERS = [
  {
    maxAmount: 50000,
    documents: [],
  },
  {
    maxAmount: 200000,
    documents: ['financial_statements_1'],
  },
  {
    maxAmount: 300000,
    documents: [
      'financial_statements_1',
      'ytd_balance_sheet',
      'ytd_profit_loss',
    ],
  },
  {
    maxAmount: Infinity,
    documents: [
      'financial_statements_2',
      'ytd_balance_sheet',
      'ytd_profit_loss',
    ],
  },
];

const getRequiredDocuments = (tclAmount = 0, businessType = '') => {
  const tier = TCL_TIERS.find((tier) => tclAmount <= tier.maxAmount);
  const documents = [...tier.documents];

  if (businessType === NONPROFIT_CORPORATION) {
    documents.unshift(DOCUMENT_DEFINITIONS.tax_exempt.id);
  }

  return documents.map((docId) => DOCUMENT_DEFINITIONS[docId]);
};

export const FinancialVerificationProvider = ({
  children,
  tclAmount = 0,
  businessType = '',
}) => {
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [financialDocumentsCompleted, setFinancialDocumentsCompleted] =
    useState(false);

  const [bankStatementsFiles, setBankStatementsFiles] = useState([]);

  const [plaidConnected, setPlaidConnected] = useState(false);
  const [plaidConnectionData, setPlaidConnectionData] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const financialDocuments = useMemo(
    () => getRequiredDocuments(tclAmount, businessType),
    [tclAmount, businessType]
  );
  const requiredDocuments = useMemo(
    () => financialDocuments.filter((doc) => doc.isRequired),
    [financialDocuments]
  );

  const shouldShowPlaidConnection = useMemo(() => {
    return businessType !== NONPROFIT_CORPORATION;
  }, [businessType]);

  const isAnyActionCompleted = useMemo(() => {
    if (businessType === NONPROFIT_CORPORATION) {
      return financialDocumentsCompleted && bankStatementsFiles.length > 0;
    }

    return (
      (!financialDocuments?.length > 0 || financialDocumentsCompleted) &&
      (bankStatementsFiles.length > 0 || plaidConnected)
    );
  }, [
    businessType,
    financialDocuments,
    financialDocumentsCompleted,
    bankStatementsFiles,
    plaidConnected,
  ]);

  const uploadDocument = useCallback((documentId, file) => {
    setUploadedDocuments((prev) => ({ ...prev, [documentId]: file }));
  }, []);

  const removeDocument = useCallback((documentId) => {
    setUploadedDocuments((prev) => {
      const newDocs = { ...prev };
      delete newDocs[documentId];
      return newDocs;
    });
  }, []);

  useEffect(() => {
    const hasAllRequired = requiredDocuments.every(
      (doc) => uploadedDocuments[doc.id]
    );

    setFinancialDocumentsCompleted(hasAllRequired);
  }, [uploadedDocuments, requiredDocuments]);

  const uploadBankStatements = useCallback((file) => {
    setBankStatementsFiles((prev) => [...prev, file]);
  }, []);

  const removeBankStatements = useCallback((fileToRemove) => {
    setBankStatementsFiles((prev) =>
      prev.filter((file) => file !== fileToRemove)
    );
  }, []);

  const connectPlaid = useCallback((connectionData) => {
    setPlaidConnectionData(connectionData);
    setPlaidConnected(true);
  }, []);

  const disconnectPlaid = useCallback(() => {
    setPlaidConnectionData(null);
    setPlaidConnected(false);
  }, []);

  const value = {
    uploadedDocuments,
    financialDocumentsCompleted,
    bankStatementsFiles,
    plaidConnected,
    plaidConnectionData,
    isAnyActionCompleted,
    isUploading,
    uploadError,
    uploadDocument,
    removeDocument,
    uploadBankStatements,
    removeBankStatements,
    connectPlaid,
    disconnectPlaid,
    setIsUploading,
    setUploadError,
    financialDocuments,
    shouldShowPlaidConnection,
  };

  return (
    <FinancialVerificationContext.Provider value={value}>
      {children}
    </FinancialVerificationContext.Provider>
  );
};

export const useFinancialVerification = () => {
  const context = useContext(FinancialVerificationContext);
  if (!context) {
    throw new Error(
      'useFinancialVerification must be used within a FinancialVerificationProvider'
    );
  }
  return context;
};
